import * as Sentry from '@sentry/react';
import { message } from 'antd';
import useAuth from 'common/useAuth';
import Button from 'components/Button/Button';
import FlexRow from 'components/FlexRow/FlexRow';
import { FormWrapper, SubmitButton, TextAreaInput, TextInput } from 'components/forms/hook-form';
import Modal from 'components/Modal/Modal';
import ModalFooter from 'components/Modal/ModalFooter';
import { getFullName } from 'helpers/contactPersonHelpers';
import { useCustomTranslation } from 'helpers/translationHelper';
import { useValidators } from 'helpers/validation';
import React from 'react';
import type { SubmitHandler } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

interface ErrorBoundaryFormModalProps {
  resetError: () => void;
  eventId: string;
  open: boolean;
}

interface FeedbackFormValues {
  name: string;
  email: string;
  message: string;
}

const ErrorBoundaryFormModal = ({ eventId, open, resetError }: ErrorBoundaryFormModalProps) => {
  const { tError, tInput } = useCustomTranslation();
  const navigate = useNavigate();
  const validators = useValidators();
  const { user } = useAuth();

  const onSubmit: SubmitHandler<FeedbackFormValues> = (data) => {
    Sentry.captureFeedback({
      email: data.email,
      name: data.name,
      message: data.message,
      associatedEventId: eventId
    });
    resetError();
    message.success(tError('error_boundary_thank_you'));
    navigate('/');
  };

  const handleClose = () => {
    resetError();
    navigate('/');
  };

  return (
    <Modal open={open} onClose={handleClose} title={tError('error_boundary_form')}>
      <FormWrapper<FeedbackFormValues>
        onSubmit={onSubmit}
        defaultValues={{
          name: user ? getFullName(user) : '',
          email: user?.email,
          message: ''
        }}
      >
        <TextInput
          name="name"
          label={tInput('first_name_input')}
          required
          options={{
            required: validators.required
          }}
        />
        <TextInput
          name="email"
          type="email"
          label={tInput('email_contact_placeholder')}
          required
          options={{
            required: validators.required
          }}
        />
        <TextAreaInput
          name="message"
          label={tError('error_boundary_explain')}
          required
          options={{
            required: validators.required
          }}
        />
        <ModalFooter>
          <FlexRow noMargin>
            <Button onClick={handleClose}>{tError('error_boundary_do_not_send_report')}</Button>
            <SubmitButton label={tError('error_boundary_send_report')} />
          </FlexRow>
        </ModalFooter>
      </FormWrapper>
    </Modal>
  );
};

export default ErrorBoundaryFormModal;
